// legendParse.js — one assembly per tag symbol on a wall / ceiling type legend (pure JS, main thread or worker).
//
// A legend sheet (e.g. "WALL ASSEMBLY SCHEDULE") is laid out in panels. Each panel has a heading
// ("INT NON-LOAD BEARING STEEL STUD WALLS"), sometimes items common to the whole panel ("- 12.7mm GYP BD
// (BOTH SIDES OF STUDS)", "- STEEL STUDS AT 400 o/c"), and tag symbols (◇ MS.12) with their own lines to the
// right ("- 92mm STUDS") or — for a row of symbols — below the row ("SAME AS ABOVE EXCEPT …"). Types that
// only exist as symbols ("MS.12f") are explained by a note ("ADD 22mm METAL FURRING TO WALL TYPES WITH 'f'
// DESIGNATION"). The same tag is often drawn again beside its section drawing with its UL / STC numbers.
//
//   parseLegend(sheet, { page, tagKinds? }) → [{ tag, kind, name, heading, description, layers[], own[],
//      base, except, fireRating, stc, ul, refs[], box, panel, page, source: "legend", verified: true }]
//
// Every symbol becomes an assembly — nothing is dropped because its text is short or missing.
// Units: sheet units (PDF points at scale 1, y down). Uses sheet.text, sheet.segs from pdfVectors.extractSheet.

import { textSymbols, axisLines, SYM, SYM_NAME } from "./sheetPrep.js";

const TAGLIKE = /^[A-Z0-9]{1,4}(?:[.\-][A-Z0-9]{1,5})?[a-z]{0,3}$/i;
const HEADING =
  /\b(WALLS?|PARTITIONS?|CEILINGS?|SOFFITS?|BULKHEADS?|SHAFT|FURRING|ASSEMBL(Y|IES)|FLOORS?|ROOFS?|LINERS?|DEMISING|CHASE|COLUMN|ENCLOSURES?|MASONRY|CONCRETE|CONC)\b/;
const KIND_OF = [
  [
    "ceiling",
    /\b(CEILING|CLG|SOFFIT|BULKHEAD|T-?BAR|ACT\b|SUSPENDED|ACOUSTIC(AL)? TILE)\b/i,
  ],
  ["floor", /\b(FLOOR ASSEMBL|SLAB)\b/i],
  ["roof", /\bROOF\b/i],
  [
    "wall",
    /\b(WALL|PARTITION|STUD|SHAFT|FURRING|MASONRY|LINER|DEMISING|CHASE|CONC|BLOCK)\b/i,
  ],
];
// mostly capitals ("INTERIOR 2 HR FIRE RATED and STC 55" is a heading)
const capsy = (s) => {
  const L = String(s).replace(/[^A-Za-z]/g, "");
  return L.length > 0 && L.replace(/[^A-Z]/g, "").length / L.length >= 0.8;
};
const squash = (x) =>
  String(x || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
const fireOf = (txt) => {
  const m = txt.match(/\b(\d(?:\.\d)?)\s*-?\s*(HR|HOUR)S?\b/i);
  if (m) return `${m[1]} HR`;
  return /\bFIRE[- ]?RATED\b/i.test(txt)
    ? "RATED"
    : /\bNOT RATED\b/i.test(txt)
      ? "NOT RATED"
      : null;
};
const stcOf = (txt) =>
  (txt.match(/\bSTC\s*[:=]?\s*(\d{2}(?:\s*-\s*\d{2})?)/i) || [])[1]?.replace(
    /\s+/g,
    "",
  ) || null;
const ulOf = (txt) => {
  const out = [];
  const re =
    /\b(?:ULC?|UL\/ULC)\s*-?\s*(?:DESIGN\s*)?(?:NO\.?\s*)?([A-Z]{1,2}\s?-?\d{3,4}(?:\.[A-Z])?)|\b(?:GA\s*)?#?\s*(WP\s?\d{3,4})\b|\b(?:RAL|NRC)\s*(TL-?\d{2}-?\d{2,4})/gi;
  let m;
  while ((m = re.exec(txt)))
    out.push((m[1] || m[2] || m[3]).replace(/\s+/g, ""));
  return [...new Set(out)];
};

// text items → lines (pieces on one baseline, close together)
function toLines(text) {
  const it = text
    .filter((t) => t.rot === 0 || t.rot === undefined)
    .map((t) => ({
      ...t,
      s: String(t.str || "").trim(),
      h: Math.max(1, t.y1 - t.y0),
    }))
    .filter((t) => t.s)
    .sort((a, b) => a.y0 - b.y0 || a.x0 - b.x0);
  const out = [];
  for (const t of it) {
    const L = out.find(
      (l) =>
        Math.abs(l.y0 - t.y0) < t.h * 0.4 &&
        t.x0 - l.x1 < t.h * 1.2 &&
        t.x0 > l.x0 - t.h * 0.5,
    );
    if (L) {
      L.s += (t.x0 - L.x1 > t.h * 0.2 ? " " : "") + t.s;
      L.x1 = Math.max(L.x1, t.x1);
      L.y1 = Math.max(L.y1, t.y1);
    } else out.push({ s: t.s, x0: t.x0, y0: t.y0, x1: t.x1, y1: t.y1, h: t.h });
  }
  return out;
}

/** "- 12.7mm GYP BD (BOTH SIDES OF" + "STUDS)" → items; a line starting with "-" / "•" / a number starts an item */
function itemsOf(lines) {
  const items = [];
  for (const l of lines) {
    const s = l.s.replace(/\s+/g, " ").trim();
    if (!s) continue;
    const starts = /^[-–•*]\s*/.test(s) || !items.length;
    const clean = s.replace(/^[-–•]\s*/, "");
    if (starts) items.push(clean);
    else items[items.length - 1] += ` ${clean}`;
  }
  return items
    .map((x) =>
      x
        .replace(/\s+/g, " ")
        .replace(/\s*\bNOTE:?\s*$/i, "")
        .trim(),
    )
    .filter((x) => x && !/^NOTE:?$/i.test(x));
}

// the box of the legend panel a point sits in (long vertical dividers; horizontal lines spanning the panel close it)
function makePanelOf(sheet) {
  const { h: hl, v: vl } = axisLines(sheet, 0.03);
  return (x, y) => {
    let L = 0,
      R = sheet.width,
      T = 0,
      B = sheet.height;
    for (const l of vl) {
      // (a divider runs most of a legend row; the outline of a section drawing in a panel is shorter)
      if (l.a > y || l.b < y || l.b - l.a < sheet.height * 0.2) continue;
      if (l.o <= x && l.o > L) L = l.o;
      if (l.o >= x && l.o < R) R = l.o;
    }
    for (const l of hl) {
      if (l.a > L + 4 || l.b < R - 4) continue; // spans the whole panel
      if (l.o <= y && l.o > T) T = l.o;
      if (l.o >= y && l.o < B) B = l.o;
    }
    return [L, T, R, B];
  };
}

/**
 * Every panel of a legend sheet that holds text (so a type drawn without a tag symbol — "X.6" in a box beside
 * its section — still gets read): [[x0,y0,x1,y1], …] — the title block, notes columns and the sheet border
 * left out.
 */
export function legendPanelBoxes(sheet, { exclude = [] } = {}) {
  const panelOf = makePanelOf(sheet);
  const W = sheet.width,
    H = sheet.height;
  const text = (sheet.text || []).filter((t) => t.str && t.str.trim());
  const seen = new Map();
  for (let x = W * 0.02; x < W; x += Math.max(40, W / 60))
    for (let y = H * 0.02; y < H; y += Math.max(40, H / 40)) {
      const b = panelOf(x, y);
      const area = (b[2] - b[0]) * (b[3] - b[1]);
      if (
        area > W * H * 0.3 ||
        b[2] - b[0] < W * 0.03 ||
        b[3] - b[1] < H * 0.05
      )
        continue;
      const k = b.map(Math.round).join(",");
      if (seen.has(k)) continue;
      const n = text.filter((t) => {
        const mx = (t.x0 + t.x1) / 2,
          my = (t.y0 + t.y1) / 2;
        return mx > b[0] && mx < b[2] && my > b[1] && my < b[3];
      }).length;
      seen.set(k, { box: b, n });
    }
  const ov = (a, b) =>
    Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0])) *
    Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]));
  const ar = (b) => (b[2] - b[0]) * (b[3] - b[1]);
  const list = [...seen.values()]
    .filter(
      ({ box, n }) =>
        n >= 5 && !exclude.some((e) => ov(box, e) > ar(box) * 0.5),
    )
    .sort((a, b) => ar(b.box) - ar(a.box));
  // a piece inside a bigger panel already kept is read with it
  const kept = [];
  for (const p of list)
    if (!kept.some((k) => ov(k.box, p.box) > ar(p.box) * 0.7)) kept.push(p);
  return kept.sort((a, b) => b.n - a.n).map(({ box }) => box);
}

export function parseLegend(sheet, { page = null, tagKinds = null } = {}) {
  const text = sheet.text || [];
  const tagLike = (t) => {
    const s = String(t.str || "").replace(/\s+/g, "");
    return (
      TAGLIKE.test(s) &&
      /[A-Z]/i.test(s) &&
      (/\d|\./.test(s) || /^[A-Z]{3,6}[a-z]{0,3}$/.test(s))
    );
  };
  const sym = textSymbols(sheet, text, tagLike);
  // which outline the tags use on this sheet: the most common one among tag-like labels (≥ 3)
  const cnt = {};
  text.forEach((t, i) => {
    if (sym[i] && sym[i] !== SYM.circle && tagLike(t) && /\d|\./.test(t.str))
      cnt[sym[i]] = (cnt[sym[i]] || 0) + 1;
  });
  let kinds = tagKinds ? tagKinds.map((k) => SYM[k]).filter(Boolean) : null;
  if (!kinds) {
    const top = Object.entries(cnt).sort((a, b) => b[1] - a[1])[0];
    kinds =
      top && top[1] >= 3
        ? [Number(top[0])]
        : [SYM.diamond, SYM.hexagon, SYM.square, SYM.oval, SYM.octagon];
  }
  const tags = [];
  const tagIdx = new Set();
  text.forEach((t, i) => {
    // (a label with no outline that leads a text block — "X.1  INTERIOR … WALL" — is a tag too)
    let free = false;
    if (
      !sym[i] &&
      /^[A-Z]{1,3}\.\d{1,3}[a-z]{0,3}$/i.test(String(t.str).trim())
    ) {
      const h = Math.max(1, t.y1 - t.y0);
      free = text.some(
        (o) =>
          o !== t &&
          o.x0 >= t.x1 &&
          o.x0 <= t.x1 + h * 6 &&
          Math.abs((o.y0 + o.y1) / 2 - (t.y0 + t.y1) / 2) < h * 0.8 &&
          (/^[-–•]/.test(o.str.trim()) ||
            (HEADING.test(o.str) && o.str === o.str.toUpperCase())),
      );
    }
    if (!(free || (kinds.includes(sym[i]) && tagLike(t)))) return;
    tagIdx.add(i);
    const cx = (t.x0 + t.x1) / 2,
      cy = (t.y0 + t.y1) / 2;
    const h = Math.max(1, t.y1 - t.y0);
    const r = Math.max((t.x1 - t.x0) / 2 + h * 0.9, h * 2); // half-size of the outline around the label
    tags.push({
      tag: t.str.replace(/\s+/g, ""),
      x0: cx - r,
      x1: cx + r,
      y0: cy - r,
      y1: cy + r,
      cx,
      cy,
      h,
    });
  });
  if (!tags.length) return [];

  const panelOf = makePanelOf(sheet);

  const inTag = (l) =>
    tags.some(
      (g) =>
        (l.x0 + l.x1) / 2 > g.x0 &&
        (l.x0 + l.x1) / 2 < g.x1 &&
        (l.y0 + l.y1) / 2 > g.y0 &&
        (l.y0 + l.y1) / 2 < g.y1 &&
        l.s.replace(/\s+/g, "") === g.tag,
    );
  // (the tag labels themselves are never part of a line of text)
  const lines = toLines(text.filter((_, i) => !tagIdx.has(i))).filter(
    (l) => !inTag(l),
  );

  // rows of symbols (several side by side share the text under them)
  for (const g of tags) g.panel = panelOf(g.cx, g.cy);
  const pkey = (p) => p.map((v) => Math.round(v)).join(",");
  const byPanel = new Map();
  for (const g of tags) {
    const k = pkey(g.panel);
    if (!byPanel.has(k)) byPanel.set(k, []);
    byPanel.get(k).push(g);
  }

  const raw = [];
  for (const group of byPanel.values()) {
    const [L, T, R, B] = group[0].panel;
    const pl = lines.filter(
      (l) => l.x0 >= L - 1 && l.x1 <= R + 40 && l.y0 >= T - 1 && l.y1 <= B + 1,
    );
    // rows: symbols at about the same height, close together sideways
    const sorted = [...group].sort((a, b) => a.cy - b.cy || a.cx - b.cx);
    const rows = [];
    for (const g of sorted) {
      const row = rows.find(
        (r) =>
          Math.abs(r.cy - g.cy) < g.h * 1.2 &&
          r.tags.some((o) => Math.abs(o.cx - g.cx) < (o.x1 - o.x0) * 1.6),
      );
      if (row) row.tags.push(g);
      else rows.push({ cy: g.cy, tags: [g] });
    }
    for (const r of rows) {
      r.y0 = Math.min(...r.tags.map((t) => t.y0));
      r.y1 = Math.max(...r.tags.map((t) => t.y1));
      r.x0 = Math.min(...r.tags.map((t) => t.x0));
      r.x1 = Math.max(...r.tags.map((t) => t.x1));
      r.tags.sort((a, b) => a.cx - b.cx);
    }
    rows.sort((a, b) => a.y0 - b.y0);
    // headings in the panel, top-down
    const heads = pl.filter(
      (l) =>
        HEADING.test(l.s) &&
        !/^[-–•*]/.test(l.s) &&
        capsy(l.s) &&
        l.s.split(/\s+/).length >= 2 &&
        !/^(SAME AS|NOTE|ADD |REFER|FROM )/.test(l.s),
    );
    const used = new Set();
    rows.forEach((row, ri) => {
      // where the next row of symbols starts below this one (lines past it belong to that row)
      const below = rows
        .slice(ri + 1)
        .filter((o) => o.x0 < row.x1 + (R - L) * 0.5);
      const nextY = below.length ? Math.min(...below.map((o) => o.y0)) : B;
      const single = row.tags.length === 1;
      let own;
      if (single) {
        const g = row.tags[0];
        // the block right of the symbol, from its top to the next symbol row
        own = pl.filter(
          (l) =>
            l.x0 >= g.x1 - g.h * 0.8 &&
            l.y1 > g.y0 - g.h * 0.2 &&
            l.y0 < nextY - g.h * 0.3 &&
            !used.has(l),
        );
        // a block that runs into another heading is that heading's
        const hIn = own.findIndex(
          (l, k) => k > 0 && heads.includes(l) && l.y0 > g.y1,
        );
        if (hIn > 0) own = own.slice(0, hIn);
      } else {
        // lines right of the last symbol on the row, then the block under the row
        own = pl.filter(
          (l) =>
            !used.has(l) &&
            ((l.x0 >= row.x1 - row.tags[0].h &&
              l.y1 > row.y0 &&
              l.y0 < row.y1) ||
              (l.y0 >= row.y1 - row.tags[0].h * 0.3 &&
                l.y0 < nextY - row.tags[0].h * 0.3 &&
                l.x0 >= row.x0 - row.tags[0].h * 2)),
        );
        const hIn = own.findIndex((l) => heads.includes(l));
        if (hIn >= 0) own = own.slice(0, hIn);
      }
      own.sort((a, b) => a.y0 - b.y0 || a.x0 - b.x0);
      for (const l of own) used.add(l);
      // the heading over this row (the nearest one above it, or the row's own first line), and the items common
      // to the whole panel under that heading (between it and the first symbol row below it)
      // (a type drawn only beside its section, at the top of the panel, is described by the panel's heading
      //  and list below it)
      let head =
        [...heads]
          .filter((l) => l.y0 <= row.y0 + row.tags[0].h)
          .sort((a, b) => b.y0 - a.y0)[0] ||
        (own.filter(
          (x) =>
            !/^\*|^#|\b(ULC?|GA|WP|RAL)\b|^\d{2,4}$|^FIRESTOP|^VARIES$/.test(
              x.s.trim(),
            ),
        ).length
          ? null
          : heads.find((l) => l.y0 > row.y1) || null) ||
        null;
      // a heading that wraps onto two lines starts at its first line
      while (head) {
        const up = pl.find(
          (l) =>
            l !== head &&
            capsy(l.s) &&
            !/^[-–•*#]/.test(l.s) &&
            !/\b(ULC?|WP\s?\d|GA WP|RAL)\b|ULC?W\d|^\d+$/.test(l.s) &&
            Math.abs(l.x0 - head.x0) < head.h * 1.5 &&
            head.y0 - l.y1 >= -1 &&
            head.y0 - l.y1 < head.h * 0.8,
        );
        if (!up) break;
        head = up;
      }
      if (own.length && heads.includes(own[0])) head = own[0];
      let headLines = [],
        common = [];
      if (head) {
        // a heading wraps: the caps lines right under it, same left edge
        headLines = [head];
        for (const l of pl)
          if (
            l !== head &&
            Math.abs(l.x0 - head.x0) < head.h * 1.5 &&
            l.y0 > headLines[headLines.length - 1].y0 &&
            l.y0 - headLines[headLines.length - 1].y1 < head.h * 0.8 &&
            !/^[-–•*]/.test(l.s) &&
            capsy(l.s) &&
            !/^(SAME AS|NOTE)/.test(l.s)
          )
            headLines.push(l);
        const firstRow = rows.find(
          (o) => o.y0 > headLines[headLines.length - 1].y1 - 1,
        );
        const stop = firstRow ? firstRow.y0 : B;
        common = pl.filter(
          (l) =>
            !headLines.includes(l) &&
            l.y0 > headLines[headLines.length - 1].y0 &&
            l.y1 <= stop + l.h * 0.3 &&
            Math.abs(l.x0 - head.x0) < (R - L) * 0.25 &&
            !own.includes(l),
        );
      }
      const ownTxt = own.filter((l) => !headLines.includes(l));
      for (const g of row.tags)
        raw.push({
          tag: g.tag,
          box: [g.x0, g.y0, g.x1, g.y1],
          panel: [L, T, R, B],
          heading: headLines
            .map((l) => l.s)
            .join(" ")
            .replace(/\s+/g, " ")
            .trim(),
          common: itemsOf(common),
          own: itemsOf(ownTxt),
          shared: !single,
          notes: pl.filter((l) => /DESIGNATION|^NOTE/i.test(l.s)),
          panelText: pl,
        });
    });
  }

  // ── one assembly per tag: the legend entry wins over the copy beside a section drawing (whose text is
  //    dimensions and listing numbers — kept as refs)
  const isRefLine = (s) =>
    /\b(ULC?|GA|WP|RAL|TL-|STC)\b|^\*|^#/.test(s) || /^\d{2,4}$/.test(s);
  const byTag = new Map();
  for (const e of raw) {
    const k = squash(e.tag);
    const score =
      e.own.filter((x) => !isRefLine(x)).length * 3 +
      (e.heading ? 2 : 0) +
      e.common.length;
    const prev = byTag.get(k);
    const refs = e.own.filter(isRefLine);
    if (!prev || score > prev.score) {
      // listing numbers (ULC / GA / STC) are references, not layers
      byTag.set(k, {
        ...e,
        score,
        refs: [...(prev?.refs || []), ...refs],
        own: e.own.filter((x) => !(isRefLine(x) && x.length < 40)),
      });
    } else
      prev.refs.push(
        ...refs,
        ...(e.own.filter((x) => !isRefLine(x)).length ? [] : []),
      );
  }
  const entries = [...byTag.values()];
  const find = (t) => byTag.get(squash(t));

  // ── resolve "SAME AS …": "SAME AS ABOVE" = the type with the same tag less its suffix (MS.12a → MS.12);
  //    "SAME AS MS.F1 EXCEPT …" = that type; a symbol with no text = its base type + the note about its suffix
  const baseOf = (tag) => {
    let t = tag;
    while (/[a-z]$/.test(t)) {
      t = t.slice(0, -1);
      if (find(t) && squash(t) !== squash(tag)) return find(t);
    }
    // MS.F1cf → MS.F1c → MS.F1 (upper-case suffix letters too)
    t = tag;
    while (t.length > 2) {
      t = t.slice(0, -1);
      if (find(t) && squash(t) !== squash(tag)) return find(t);
    }
    return null;
  };
  const resolved = new Map();
  const resolve = (e, depth = 0) => {
    if (resolved.has(e)) return resolved.get(e);
    const ownTxt = e.own.join(" ");
    let base = null,
      except = "";
    const m = ownTxt.match(
      /^SAME AS\s+(ABOVE|[A-Z0-9.\-]+[a-z]{0,3})\b\s*(?:EXCEPT\s*)?(.*)$/i,
    );
    if (m) {
      base = /^ABOVE$/i.test(m[1]) ? baseOf(e.tag) : find(m[1]);
      except = m[2].replace(/\s*\bNOTE:?\s*$/i, "").trim();
    } else if (!e.own.length) {
      base = baseOf(e.tag);
      // the note about this tag's suffix ("… WALL TYPES WITH 'f' DESIGNATION")
      const suf = e.tag.slice(String(base?.tag || "").length);
      const note = e.notes.find(
        (n) =>
          suf &&
          new RegExp(`['"‘’]${suf.slice(-1)}['"‘’]\\s*DESIGNATION`, "i").test(
            n.s,
          ),
      );
      if (note) {
        const at = e.panelText.indexOf(note);
        const block = e.panelText
          .slice(Math.max(0, at - 2), at + 2)
          .filter((l) => !/^NOTE:?$/i.test(l.s));
        except = block
          .map((l) => l.s)
          .join(" ")
          .replace(/\s+/g, " ");
      }
    }
    let out;
    if (base && base !== e && depth < 4) {
      const b = resolve(base, depth + 1);
      out = {
        heading: e.heading || b.heading,
        items: [...b.items, ...(except ? [`EXCEPT: ${except}`] : [])],
        base: base.tag,
        except,
      };
    } else
      out = {
        heading: e.heading,
        items: [
          ...e.common,
          ...e.own.filter((x) => !/^SAME AS/i.test(x) || true),
        ],
        base: null,
        except: "",
      };
    resolved.set(e, out);
    return out;
  };

  return entries
    .map((e) => {
      const r = resolve(e);
      const all = [r.heading, ...r.items, ...e.refs].join(" \n ");
      const kind = (KIND_OF.find(([, re]) =>
        re.test(`${r.heading} ${r.items.join(" ")}`),
      ) || ["wall"])[0];
      // the name: the panel heading, with what makes this type different
      const own = e.own
        .filter((x) => !/^SAME AS/i.test(x))
        .filter((x) => x.length <= 40);
      const name = (r.heading || own[0] || e.tag)
        .replace(/\s+/g, " ")
        .slice(0, 80);
      return {
        tag: e.tag.toUpperCase(),
        tagRaw: e.tag,
        kind,
        name,
        heading: r.heading,
        description: r.items.join("\n").slice(0, 1500),
        layers: r.items.filter((x) => !/^EXCEPT:/.test(x)).slice(0, 30),
        own: e.own,
        base: r.base,
        except: r.except,
        fireRating: fireOf(all),
        stc: stcOf(all),
        ul: ulOf(all).join(", ") || null,
        refs: [...new Set(e.refs)],
        box: e.box,
        panel: e.panel,
        page,
        source: "legend",
        verified: true,
      };
    })
    .sort((a, b) => a.panel[0] - b.panel[0] || a.box[1] - b.box[1]);
}

export { SYM_NAME };
